import { useCallback, useMemo } from 'react';
import {
  PRESCRIBER,
} from '../../constants/patientChart.constant';
import {
  NavBar, NavKey, PageTitle, enabledNavItems,
} from '../../components/patientChart';
import { PageHeading, useAvatarTitle } from '../../components/PageHeading';
import LinearProgress from '../../components/LinearProgress';
import { Listing } from '../../components/tasks';
import { TasksControllerHOC } from '../../controllers/tasks';
import useNavItems from '../../hooks/useNavItems';
import useQueryParams from '../../hooks/useQueryParams';
import useVisits from '../../hooks/useVisits';
import { FEATURE } from '../../utils/feature';
import Logo from '../../assets/logo.png';
import '../patientChart/styles.css';

const hasFeature = FEATURE();

const SelectedNavItem = {
  key: NavKey.tasks
};

const NavItems = enabledNavItems([
  NavKey.sv,
  NavKey.ao,
  NavKey.tasks,
  NavKey.pcm,
  NavKey.so,
]);

const BottomNavItems = enabledNavItems([
  NavKey.v1,
]);

/**
 * @param {{
 *    tasksInfo: {
 *      data: {
 *        Id: string,
 *      }[],
 *      called: boolean,
 *      loading: boolean,
 *    };
 * }} props 
 * @returns {React.JSX.Element}
 */
export const Tasks = ({
  history,
  location,
  scheduledVisits = {},
  asyncOrders = {},
  selectedPrescriberInfo: prescriber = PRESCRIBER(),
  headerInfoAPI = {},
  tasksInfo = {},

  GetScheduleVisitsLV: getScheduledVisits = async () => {},
  GetPendingFilesLV: getPendingVisits = async () => {},
  GetTasks: getTasks = async () => {},
  GetHeaderInfo: getPrescriber = async () => {},
  LogOff: logOff = async () => ({}),
}) => {
  const { json: { version: queryVersion = 1 } } = useQueryParams({ location });

  const { navItems, bottomNavItems } = useNavItems({
    scheduledVisits: scheduledVisits?.visits?.data || [],
    asyncOrders: asyncOrders?.visits?.data || [],
    tasks: tasksInfo?.data || [],
    items: NavItems,
    bottomItems: BottomNavItems,
    history,
  });

  const { avatarTitle } = useAvatarTitle(prescriber);

  useVisits({
    prescriber,
    scheduledVisits,
    asyncOrders,
    headerInfoAPI,
    tasksInfo,
    getTasks,
    getPrescriber,
    getPendingVisits,
    getScheduledVisits,
  });

  const handleRoute = useCallback((url = '') => {
    history.push(url);
  }, [history]);

  const handleChange = useCallback((data = {}) => {
    if (!data?.userId) {
      return false;
    }

    if (!data?.orderId) {
      return false;
    }

    const route = `/patientchart/${data.orderId}/${data.userId}?tab=tasks&taskId=${data?.taskId}`;

    if (hasFeature.PATIENT_CHART_V3 && queryVersion === 3) {
      handleRoute(`${route}&version=3`);
      return true;
    }

    if (hasFeature.PATIENT_CHART_V2) {
      handleRoute(`${route}&version=2`);
    }
    return true;
  }, [handleRoute]);

  const PageHeadingComponent = new Map([
    [true, (
      <PageHeading
        logo={Logo}
        message={prescriber.Message}
        avatarTitle={avatarTitle}
        hasActions
        logOff={logOff}
        handler={{
          route: handleRoute,
        }}
      />
    )],
    [false, <></>]
  ]);

  const NavBarComponent = new Map([
    [true, <NavBar items={navItems} bottomItems={bottomNavItems} selectedItem={SelectedNavItem} />],
    [false, <></>]
  ]);

  const isLoading = useMemo(() => {
    if (!tasksInfo.called) {
      return true;
    }

    return tasksInfo.loading;
  }, [tasksInfo]);

  return (
    <div className='patient-chart-container flex flex-column flex-fill'>
      <LinearProgress loading={isLoading} />

      {PageHeadingComponent.get(hasFeature.PAGE_HEADING)}

      <div className='patient-chart-content flex'>
        {NavBarComponent.get(hasFeature.NAV_BAR)}

        <div className='flex flex-column flex-fill gap-24'>
          <PageTitle title="Tasks" type={3} loading={isLoading} onRefresh={getTasks} />

          <div className='flex flex-column flex-fill gap-24 p-l-6 p-r-6'>
            <Listing
              tasksInfo={tasksInfo}
              handleRouteChange={handleChange}
              onRefresh={getTasks}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TasksControllerHOC(Tasks);
